<script>
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import tabIcon from "@/assets/narvarostatistik.svg";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import ActionIconOpanmalan from "@/assets/actionIcons/show.svg";
    import SummaryTable from "@/tabs/Skreg/Statistik/SummaryTable.vue";
    import NarvarostatistikSpelare from "@/tabs/Skreg/Statistik/NarvarostatistikSpelare.vue";
    import NarvarostatistikTableRow from "@/tabs/Skreg/Statistik/NarvarostatistikTableRow.vue";
    import { startAsyncTask } from "@/utils/asyncTaskPoller";
    import JournalAnteckning from "@/tabs/Journalanteckningar/Journalanteckning.vue";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";
    import moment from "moment";
    import IdrottPopup from "@/tabs/Skreg/Statistik/IdrottPopup.vue";
    import KHeader from "@/components/KHeader.vue";
    import { NARVARO_HEADERS, returnValueIfNotUndefined } from "@/tabs/Skreg/Statistik/narvarostatistikConstants.js";
    import { getErrorMessage } from "@/api";
    import { openDialog } from "@/utils";

    export default {
        extends: BaseTab,
        name: "NarvarostatistikLag",
        tabName: "Närvarostatistik lag",
        icon: tabIcon,

        components: {
            KFilter,
            SummaryTable,
            NarvarostatistikTableRow,
            JournalAnteckning,
            ModalBasic,
            IdrottPopup,
            KHeader,
        },

        data() {
            return {
                searchFilterValues: null,
                defaultSearchValues: null,
                headers: NARVARO_HEADERS,
                rows: [],
                summaryTableData: {},
                toggleRow: false,
                dropdownActions: [
                    {
                        title: "Närvarostatistik spelare",
                        name: "visanarvarostatistikspelare",
                        icon: ActionIconOpanmalan,
                        show: () => true,
                    },
                    {
                        title: "Journalanteckningar skadekort",
                        name: "visajournalanteckningarskadekort",
                        icon: ActionIconOpanmalan,
                        show: (item) => (item.data.sista_oavstamd_skadekort ? true : false),
                    },
                ],
                idrottTotal: 0,
                idrott: "",
                idrottId: "",
                BASE_URL: "/skreg/statistik/idrotter/",
                showNoDataMessage: false,
            };
        },

        computed: {
            filterSchema() {
                return generateSchema({
                    addToAll: { ignoreEmptyData: true },
                    schema: [
                        {
                            component: "LagAutocomplete",
                            name: "lag",
                            label: "Lag",
                            idrottId: this.idrottId,
                            includeAllOption: true,
                            placeholder: "Välj lag",
                            validation: "required",
                            autoPosition: true,
                            tagValueFormatter: (value) =>
                                value === "allalag"
                                    ? "Alla lag"
                                    : (this.$store.getters["skreg/lag/lagById"]({ id: value })?.namn ?? value),
                        },
                        {
                            component: "KDateTimepicker",
                            name: "fromDate",
                            label: "Från datum",
                            placeholder: "Välj datum",
                            validation: "required",
                        },
                        {
                            component: "KDateTimepicker",
                            name: "toDate",
                            label: "Till datum",
                            placeholder: "Välj datum",
                            validation: "required",
                        },
                    ],
                });
            },
        },

        methods: {
            showAllRows() {
                this.toggleRow = !this.toggleRow;
            },

            async onSearchSubmit({ formData }) {
                this.searchFilterValues = formData;
                await this.searchLagWithQueryParams();
            },

            tableRowClicked(item) {
                if (item.patient) {
                    this.selectedItem = item;
                    this.$store.dispatch("patientData/loadFromBackend", {
                        id: item.patient,
                        failSilently: false,
                    });
                }
            },

            async dropdownaction({ action, item }) {
                switch (action) {
                    case "visanarvarostatistikspelare":
                        await this.$store.dispatch("tabs/closeTab", NarvarostatistikSpelare, { root: true });
                        await this.$store.dispatch("patientData/loadFromBackend", {
                            id: item.spelare.patient.toString(),
                        });
                        await this.$store.dispatch("tabs/openTab", {
                            component: NarvarostatistikSpelare,
                            parameters: { ...item, idrott: this.idrott, pk: item.spelare.patient },
                        });
                        break;
                    case "visajournalanteckningarskadekort":
                        let id = item.data.sista_oavstamd_skadekort;
                        await this.$store.dispatch("tabs/closeTab", JournalAnteckning, { root: true });
                        await this.$store.dispatch("journalanteckningar/getJournalanteckning", id);
                        let anteckning = this.$store.getters["journalanteckningar/anteckning"];
                        await this.$store.dispatch("patientData/loadFromBackend", { id: anteckning.patient.id });
                        await this.$store.dispatch("tabs/openTab", { component: JournalAnteckning });
                        break;
                    default:
                        break;
                }
            },

            returnValueIfNotUndefined,

            async searchLagWithQueryParams() {
                let url_query_parameters = `?lag=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.lag
                )}&tidpunkt__date__gte=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.fromDate
                )}&tidpunkt__date__lte=${this.returnValueIfNotUndefined(this.searchFilterValues?.toDate)}`;

                if (this.searchFilterValues.lag === "allalag") {
                    const dialog_response = await this.showVarningsPopup({
                        cancelButton: "Avbryt",
                        okButton: "Ja, gå vidare",
                        title: "Du har valt att söka på Alla lag i din förening. Detta kan innebära att du visar medicinsk info för spelare du inte har en vårdrelation till. Vill du gå vidare med sökningen?",
                    });

                    if (dialog_response) {
                        await this.$store.dispatch("tabs/displaySpinner", {
                            id: this.tabId,
                            display: true,
                            ignoreTimeout: true,
                        });
                        await this.getNarvaroDataInterval(url_query_parameters);
                    }
                } else {
                    await this.$store.dispatch("tabs/displaySpinner", {
                        id: this.tabId,
                        display: true,
                        ignoreTimeout: true,
                    });
                    await this.getNarvaroDataInterval(url_query_parameters);
                }
            },
            filterByShirtNumber() {
                const filteredData = this.rows.filter((item) => {
                    return (
                        item.spelare &&
                        item.spelare.spelarelag &&
                        item.spelare.spelarelag[0] &&
                        item.spelare.spelarelag[0].trojnummer !== undefined
                    );
                });

                filteredData.sort((a, b) => a.spelare.spelarelag[0].trojnummer - b.spelare.spelarelag[0].trojnummer);

                this.rows = filteredData;
            },
            async getNarvaroDataInterval(url) {
                const requestUrl =
                    this.BASE_URL +
                    (this.idrott ?? "").toLowerCase() +
                    "/narvaro/lag/async" +
                    this.returnValueIfNotUndefined(url);

                const { promise, cancel } = startAsyncTask(requestUrl, (taskId) => requestUrl + "&task_id=" + taskId);
                this._cancelTask = cancel;

                try {
                    const taskRes = await promise;
                    if (this.$el === undefined) return;

                    this.rows = taskRes.spelare_list;
                    this.summaryTableData = taskRes;

                    if (this.searchFilterValues.lag !== "allalag") {
                        this.rows = this.rows.map((item) => ({
                            ...item,
                            spelare: {
                                ...item.spelare,
                                spelarelag: item.spelare.spelarelag.filter(
                                    (sl) => sl.lag === this.searchFilterValues.lag
                                ),
                            },
                        }));
                    }

                    this.filterByShirtNumber();
                    this.showNoDataMessage = !this.rows.some((r) => r.data?.total);
                } catch (err) {
                    if (this.$el === undefined || err?.type === "cancelled") return;

                    if (err?.type === "task_failure") {
                        await this.showVarningsPopup({
                            okButton: "OK",
                            title: "Sökningen misslyckades. Kontrollera att sökfälten är korrekt ifyllda och försök igen.",
                        });
                    } else if (err?.type === "timeout") {
                        await this.showVarningsPopup({
                            okButton: "OK",
                            title: "Sökningen tog för lång tid. Försök igen med ett kortare datumintervall.",
                        });
                    }
                } finally {
                    this._cancelTask = null;
                    if (this.$el !== undefined) {
                        await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
                    }
                }
            },

            async showVarningsPopup({ okButton, cancelButton, title }) {
                const dialog_response = await this.$refs.dialog.show({
                    variant: BasicModalVariant.WARNING,
                    okButton: okButton,
                    title: title,
                    cancelButton: cancelButton && cancelButton,
                });
                return dialog_response;
            },

            chooseIdrott(idrottId) {
                this.idrottId = idrottId;
                this.idrott = this.$store.getters["skreg/idrott/idrottById"]({ id: idrottId })?.namn ?? idrottId;
            },
        },

        async created() {
            const firstDate = moment().startOf("month");
            const lastDate = moment().endOf("month");
            this.defaultSearchValues = {
                fromDate: firstDate.format("YYYY-MM-DD"),
                toDate: lastDate.format("YYYY-MM-DD"),
            };
            this.searchFilterValues = { ...this.defaultSearchValues };

            try {
                const { subsetName, total } = await this.$store.dispatch("skreg/idrott/fetchIdrott", {});
                this.idrottTotal = total ?? 0;
                if (this.idrottTotal === 1) {
                    const [idrott] = this.$store.getters["skreg/idrott/idrottSubset"]({ subsetName, limit: 0 });
                    this.idrottId = idrott.id;
                    this.idrott = idrott.namn;
                }
            } catch (e) {
                openDialog("Det gick inte att hämta idrotter." + getErrorMessage(e), "warning");
            }
        },

        beforeDestroy() {
            this._cancelTask?.();
            this._cancelTask = null;
        },
    };
</script>

<template>
    <div class="narvarostatistik-lag">
        <KHeader heading="Närvarostatistik lag" />
        <KFilter
            name="narvaro-lag-filter"
            :schema="filterSchema"
            :defaultValue="defaultSearchValues"
            :expanded="true"
            @submit="onSearchSubmit"
            hideSearch
        />
        <SummaryTable v-if="rows.length" :summaryTableData="summaryTableData" class="summarytable" />
        <div v-if="rows.length" class="visa-alla-rader">
            <KCheckbox name="VisaAllaRader" label="Visa alla rader" @inputValue="showAllRows" />
        </div>
        <table v-if="rows.length">
            <thead class="sticky-header">
                <tr>
                    <th class="" v-for="(header, index) in headers" :key="index">
                        <div
                            v-if="header === 'Deltagare'"
                            class="header-vertical-padding-8 heading-style align-left padding-left-20"
                        >
                            {{ header }}
                        </div>
                        <div v-else class="header-vertical-padding-8 heading-style align-right">
                            {{ header }}
                        </div>
                    </th>
                </tr>
            </thead>
            <NarvarostatistikTableRow
                v-for="(row, rowIndex) in rows"
                :key="rowIndex"
                :rowData="row"
                :dropdownActions="dropdownActions"
                :toggleRow="toggleRow"
                :toggleable="true"
                @action="dropdownaction"
                @tableRowClicked="tableRowClicked"
            />
        </table>
        <div v-if="showNoDataMessage" class="no-data">
            <img src="@/assets/check_circle_black.svg" alt="black circle" /> Det finns inga avstämda aktiviteter i vald
            tidsperiod.
        </div>
        <IdrottPopup v-if="idrottTotal > 1" variant="form" @chooseIdrott="chooseIdrott" />
        <modal-basic ref="dialog" />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables.scss";

    .narvarostatistik-lag {
        .summarytable {
            max-width: 50rem;
        }

        .align-right {
            text-align: right;
        }

        .align-left {
            text-align: left;
        }

        .header-vertical-padding-8 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
        }

        .padding-left-20 {
            padding-left: 1.25rem;
        }

        .visa-alla-rader {
            display: flex;
            justify-content: flex-end;
            padding: 0.5rem;
        }

        .no-data {
            img {
                height: 20px;
                margin-right: 10px;
            }

            display: flex;
        }

        table {
            border-collapse: collapse;
            width: 100%;

            thead.sticky-header {
                background-color: $color-bg-table-header;
                position: -webkit-sticky;
                position: sticky;
                top: 0;
                z-index: 1;

                .heading-style {
                    color: $color-table-header;
                    font-family: Roboto Medium;
                    font-weight: 500;
                    line-height: 17px;
                    font-size: 14px;
                }
            }
        }

        margin-bottom: 5rem;
    }
</style>
